import React,{ useState, useContext, useEffect } from "react";
import { Dropdown, Icon, Stack, TextField, Toggle, TooltipHost } from "@fluentui/react";
import { SendRegular } from "@fluentui/react-icons";
import Send from "../../assets/Send.svg";
import styles from "./QuestionInput.module.css";
import { AppStateContext } from "../../state/AppProvider";
import { ChatMessage, ToolInfo } from "../../api";
import { useIsMobile } from "../../hooks/useIsMobile";
import { buildDefaultArgs } from "../../utils";

interface ButtonStyle {
    color: string
    backgroundColor: string
}

interface ButtonStateStyle {
    baseStyle: ButtonStyle
    hoverStyle: ButtonStyle
    activeStyle: ButtonStyle
    activeHoverStyle: ButtonStyle
}

export const BasicButtonStyle: ButtonStateStyle = {
    baseStyle:        { color: "#3b3b3b", backgroundColor: "transparent" },
    hoverStyle:       { color: "#3b3b3b", backgroundColor: "#a1c0be10" },
    activeStyle:      { color: "#0F6CBD", backgroundColor: "#8DDDD840" },
    activeHoverStyle: { color: "#0F6CBD", backgroundColor: "#80dbd548"}
}

const PromptTemplateButtonStyle: ButtonStateStyle = {
    baseStyle:        { color: "#3b3b3b", backgroundColor: "transparent" },
    hoverStyle:       { color: "#3b3b3b", backgroundColor: "#a1c0be10" },
    activeStyle:      { color: "#bd860f", backgroundColor: "#8DDDD840" },
    activeHoverStyle: { color: "#bd860f", backgroundColor: "#80dbd548"}
}

interface OptionButtonProps {
    isAvailable: boolean // そもそも利用可能か
    isActive: boolean    // ONか
    text: string,
    iconName?: string
    onClick: () => void
    buttonStyle: ButtonStateStyle
    maxWidth?: string
    testId?: string
}

const OptionButton = (props: OptionButtonProps) => {
    const {isAvailable, isActive, text, iconName, onClick, buttonStyle, maxWidth, testId} = props
    const [isHovered, setIsHovered] = useState(false);
    const currentStyle = isActive 
    ? (isHovered ? buttonStyle.activeHoverStyle : buttonStyle.activeStyle) 
    : (isHovered ? buttonStyle.hoverStyle : buttonStyle.baseStyle)
    
    const optionButtonStyle = {
        "border": "#8DDDD890 1px solid",
        "borderRadius": "18px",
        "padding": "5px 9px",
        "cursor": "pointer",
        "transition": "all 0.1s ease-in-out",
        "maxWidth": maxWidth,
        "height": "36px",
        ...currentStyle,
    }
    return (
        isAvailable ?
        <Stack 
            horizontal 
            verticalAlign="center"
            style={optionButtonStyle}
            role="button"
            tabIndex={0}
            aria-label="Ask question button"
            aria-pressed={isActive}
            data-testid={testId}
            onClick={isAvailable ? onClick : (() => {})}
            aria-disabled={!isAvailable}
            tokens={{childrenGap: "5px"}}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {
                iconName && <Icon
                    iconName={iconName}
                    style={{userSelect: "none"}}
                />
            }
            <label className={`${styles.optionButtonLabel}`}>
                {text}
            </label>
        </Stack> : <></>)
}

const renderSchemaField = (
    key: string,
    schemaProp: any,
    value: any,
    onChange: (k: string, v: any) => void,
    disabled: boolean
) => {
    const label = schemaProp.title ?? key
    const desc  = schemaProp.description ?? ''

    if (schemaProp.enum) {
        const options = (schemaProp.enum as any[]).map((e: any) => ({ key: String(e), text: String(e) }))
        return (
            <Dropdown
                key={key}
                label={label}
                title={desc}
                options={options}
                selectedKey={value !== undefined ? String(value) : options[0]?.key}
                onChange={(_: any, opt: any) => onChange(key, opt?.key)}
                disabled={disabled}
                styles={{
                    root: { minWidth: 100 },
                    label: { color: "#3b3b3b", fontWeight: 500 },
                    dropdown: { borderRadius: 18 },
                    title: {
                        border: "#8DDDD890 1px solid",
                        borderRadius: 18,
                        backgroundColor: "transparent",
                        color: "#3b3b3b",
                        height: 36,
                        lineHeight: "24px",
                        padding: "5px 36px 5px 9px",
                        selectors: {
                            ":hover": {
                                backgroundColor: "#a1c0be10",
                                borderColor: "#8DDDD890"
                            },
                            ":focus": {
                                borderColor: "#0F6CBD"
                            }
                        }
                    },
                    caretDownWrapper: { height: 36 },
                    caretDown: { color: "#3b3b3b" },
                    dropdownItemSelected: { backgroundColor: "#8DDDD840" }
                }}
            />
        )
    }
    if (schemaProp.type === 'boolean') {
        return (
            <Toggle
                key={key}
                label={label}
                title={desc}
                checked={!!value}
                onChange={(_: any, checked?: boolean) => onChange(key, !!checked)}
                disabled={disabled}
            />
        )
    }
    if (schemaProp.type === 'integer' || schemaProp.type === 'number') {
        const minimum = typeof schemaProp.minimum === 'number'
            ? schemaProp.minimum
            : (typeof schemaProp.minimum === 'string' && schemaProp.minimum.trim() !== '' && Number.isFinite(Number(schemaProp.minimum))
                ? Number(schemaProp.minimum)
                : undefined)
        const maximum = typeof schemaProp.maximum === 'number'
            ? schemaProp.maximum
            : (typeof schemaProp.maximum === 'string' && schemaProp.maximum.trim() !== '' && Number.isFinite(Number(schemaProp.maximum))
                ? Number(schemaProp.maximum)
                : undefined)

        return (
            <TextField
                key={key}
                label={label}
                title={desc}
                type="number"
                value={value !== undefined ? String(value) : ''}
                onChange={(_, v) => {
                    if (v === '' || v === undefined) {
                        onChange(key, undefined)
                        return
                    }

                    const parsed = Number(v)
                    if (!Number.isFinite(parsed)) {
                        return
                    }

                    let next = parsed
                    if (minimum !== undefined) {
                        next = Math.max(next, minimum)
                    }
                    if (maximum !== undefined) {
                        next = Math.min(next, maximum)
                    }
                    if (schemaProp.type === 'integer') {
                        next = Math.trunc(next)
                    }

                    onChange(key, next)
                }}
                disabled={disabled}
                min={minimum}
                max={maximum}
                step={schemaProp.type === 'integer' ? 1 : 'any'}
                styles={{ root: { width: 200 } }}
            />
        )
    }
    // default: string
    return (
        <TextField
            key={key}
            label={label}
            title={desc}
            value={value ?? ''}
            onChange={(_, v) => onChange(key, v ?? '')}
            disabled={disabled}
            styles={{ root: { minWidth: 200 } }}
            multiline
            rows={5}
        />
    )
}

interface Props {
    onSend: (question: string, id: string | undefined, tools: string[], toolArgs: Map<string, Map<string, any>>) => void;
    disabled: boolean;
    sendNotAllowed: boolean
    placeholder?: string;
    clearOnSend?: boolean;
    conversationId?: string;
    question: string; // 親から渡されたquestion
    setQuestion: React.Dispatch<React.SetStateAction<string>>; // 親から渡されたsetQuestion
    files: File[]
    isFileSelected: boolean
    messages: ChatMessage[];
    maxHeight?: string;
    tools: string[];
    setTools: (value: string[]) => void;
    toolArgs: Map<string, Map<string, any>>;
    setToolArgs: (toolName: string, argName: string, value: any) => void;
    // data-testid の接頭辞（同じ画面に複数表示される場合に区別するため）
    testIdPrefix?: string;
}

export const QuestionInput = ({ 
    onSend, disabled, sendNotAllowed, placeholder, clearOnSend, conversationId, question, setQuestion, isFileSelected, messages, files, maxHeight,
    tools, setTools, toolArgs, setToolArgs, testIdPrefix = ""
}: Props) => {

    const appStateContext = useContext(AppStateContext);
    const modelIndex = appStateContext?.state?.selectedGPTModelIndex
    const modelinfo = modelIndex ? appStateContext?.state.availableModels.get(modelIndex) : undefined
    const isMobile = useIsMobile()
    const prmtmp = appStateContext?.state.promptTemplateOverride
    const prmtmpName = prmtmp ? prmtmp.name : "プロンプトテンプレート"

    useEffect(() => {
        const handler = (e: MessageEvent<any>) => {
            if(!e.isTrusted) return
            if(e.origin !== window.location.origin) return
            if(typeof e.data !== "string") return
            setQuestion(e.data)
        }
        window.addEventListener('message', handler)

        return () => {
            window.removeEventListener("message", handler)
        }
    }, [])

    // モデルが変更されたら、利用可能なツール状況に応じてツール状態を変更する
    useEffect(() => {
        const toolsFiltered = tools.filter(t => modelinfo?.tools.map(tt => tt.name).includes(t))
        setTools(toolsFiltered)
    }, [modelinfo])

    // ツールが選択されたら、そのツールのデフォルトtoolArgsを構築する
    useEffect(() => {
        for (const tool of tools) {
            const toolInfo = modelinfo?.tools.find((v) => v.name == tool)
            if (toolInfo === undefined || toolInfo.arg_schema === undefined) continue

            const arg = toolArgs.get(tool)
            
            const schema = toolInfo.arg_schema
            const defaults = buildDefaultArgs(schema)
            for (const [k, v] of defaults.entries()) {
                if (arg === undefined || !arg.has(k)) {
                    setToolArgs(tool, k, v)
                }
            }
        }
    }, [tools])

    const sendQuestionDisabled = sendNotAllowed || !question.trim();

    const disablePromptTemplateOverride = () => {
        appStateContext?.dispatch({type: "SET_PROMPT_TEMPLATE_OVERRIDE", payload: null})
    }
    const onClickTool = (tool_name: string) => {
        if (tools.includes(tool_name)){
            setTools(tools.filter(v => v !== tool_name))
        }
        else {
            setTools([...tools, tool_name])
        }
        disablePromptTemplateOverride()
    }
    const isToolEnabled = (tool_name: string) => {
        return tools.includes(tool_name)
    }
    const isToolDisabled = (tool_name: string) => {
        if (disabled) {
            return true;
        } else if (!appStateContext?.state?.selectedGPTModelIndex || !modelinfo?.tools.map(v=>v.name).includes(tool_name)) {
            return true;
        }
        return false;
    }

    const sendQuestion = () => {
        if (sendQuestionDisabled || !question.trim()) {
            return;
        }
        onSend(question, conversationId, tools, toolArgs);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setQuestion(newValue || "");
    };

    return (
        <Stack className={styles.questionInputContainer} style={{
            backgroundColor: disabled ? "#F3F2F1" : "#FFFFFF"
        }}>
            <TextField
                className={styles.questionInputTextArea}
                placeholder={placeholder}
                multiline
                autoAdjustHeight
                resizable={false}
                borderless
                value={question}
                onChange={onQuestionChange}
                onKeyDown={onEnterPress}
                style={{maxHeight: maxHeight, overflowY: "auto"}}
                disabled={disabled}
                data-testid={`${testIdPrefix}question-input`}
            />
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{padding: "10px 20px"}}>
                <Stack tokens={{childrenGap: "6px"}} style={{width: "100%"}}>
                    <Stack horizontal horizontalAlign="space-between" style={{width: "100%", paddingRight: "20px"}}>
                        <Stack horizontal tokens={{childrenGap: "10px"}}>
                            {
                                modelinfo?.tools.map(v => (
                                    <TooltipHost
                                        content={<span style={{ whiteSpace: "pre-line" }}>{v.description ?? `${v.display_name}`}</span>}
                                        key={v.name}
                                    >
                                        <OptionButton 
                                            isAvailable={!isToolDisabled(v.name)} 
                                            isActive={isToolEnabled(v.name)} 
                                            text={isMobile ? "" : v.display_name}
                                            iconName={v.icon_name ?? "DeveloperTools"}
                                            onClick={() => onClickTool(v.name)} 
                                            buttonStyle={BasicButtonStyle}
                                            testId={`${testIdPrefix}tool-button-${v.name}`}
                                        />
                                    </TooltipHost>
                                ))
                            }
                        </Stack>
                        <OptionButton 
                            isAvailable={Boolean(prmtmp)} 
                            isActive={Boolean(prmtmp)} 
                            text={isMobile ? (prmtmpName.at(0) ?? "") : prmtmpName}
                            onClick={disablePromptTemplateOverride} 
                            buttonStyle={PromptTemplateButtonStyle}
                            maxWidth="160px"
                            testId={`${testIdPrefix}prompt-template-badge`}
                        />
                    </Stack>
                    {
                        modelinfo?.tools
                        .filter(t => tools.includes(t.name) && t.arg_schema !== null)
                        .map((toolInfo) => {
                            const tool = toolInfo.name
                            const schema = toolInfo.arg_schema
                            if (schema === null) {
                                return <></>
                            }
                            const currentArg = toolArgs.get(tool)
                            const handleArgChange = (argName: string, value: any) => {
                                setToolArgs(tool, argName, value)
                            }
                            return (<Stack key={tool} horizontal tokens={{ childrenGap: 6 }}>
                                    {Object.entries(schema.properties ?? {}).map(([k, v]) =>
                                        renderSchemaField(k, v, currentArg?.get(k), handleArgChange, false)
                                    )}
                                </Stack>)
                        })
                    }
                </Stack>
                <div
                    role="button" 
                    tabIndex={1}
                    aria-label="Ask question button"
                    aria-disabled={sendQuestionDisabled}
                    data-testid={`${testIdPrefix}send-button`}
                    onClick={sendQuestion}
                    onKeyDown={e => e.key === "Enter" || e.key === " " ? sendQuestion() : null}
                >
                    { sendQuestionDisabled ? 
                        <SendRegular className={styles.questionInputSendButtonDisabled}/>
                        :
                        <img
                            src={Send}
                            alt="Send"
                            className={`${appStateContext?.state?.isRAGEnabled ? styles.questionInputSendButton_RAG : ""}`}
                        />
                    }
                </div>
            </Stack>
            <div className={`${styles.questionInputBottomBorder} ${appStateContext?.state?.isRAGEnabled ? styles.questionInputBottomBorder_RAG : ""}`} />
        </Stack>
    );
};